/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { MdDiscount } from 'react-icons/md';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button from 'legacy/common/components/button/Button';
import DateTimeRangePicker from 'legacy/components/util/FormHelpers/DateTimeRangePicker';
import { useGetEvent } from 'legacy/events/hooks';
import dayjs from 'dayjs';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import { PromoterDiscountType } from 'legacy/events/types';
import KickbackKindButton from '../../../Kickback/components/KickbackKindButton';

const TIME_FORMAT = 'h:mm A';

type PromoterDiscountCodeFormProps = {
  eventId: string;
  initialValues?: PromoterDiscountType;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: ({
    eventId,
    value,
    kind
  }: {
    eventId: string;
    value: number;
    kind: string;
    startDate?: Date;
    endDate?: Date;
    maxUsage?: number;
  }) => void;
};

const PromoterDiscountCodeForm: React.FC<PromoterDiscountCodeFormProps> = ({
  eventId,
  errorMessage = '',
  initialValues,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation('events');
  const { data: event, isLoading } = useGetEvent(eventId);

  const PromoterDiscountCodeSchema = useMemo(() => 
    Yup.object().shape({
      amount: Yup.number()
        .required(t('settings.promoterOffers.promoterDiscount.validation.discountRequired'))
        .min(0, t('settings.promoterOffers.promoterDiscount.validation.discountMinValue'))
    }),
    [t]
  );

  const maxEndDate = useMemo(() => {
    if (isLoading || !event) {
      return null;
    }

    const effectiveEndDate = new Date(event.startDate);
    effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);

    return event.endDate ? event.endDate : effectiveEndDate.toISOString();
  }, [event, isLoading]);

  return (
    <Formik
      initialValues={{
        amount: initialValues?.value || undefined,
        kind: initialValues?.kind || 'percentage',
        startDate: initialValues?.startDate || undefined,
        endDate: initialValues?.endDate || undefined,
        startDateTime: initialValues?.startDate
          ? dayjs(initialValues?.startDate).format(TIME_FORMAT)
          : undefined,
        endDateTime: initialValues?.endDate
          ? dayjs(initialValues?.endDate).format(TIME_FORMAT)
          : undefined,
        maxUsage: initialValues?.usageLimitPerContract || undefined
      }}
      onSubmit={(values) => {
        onSubmit({
          eventId,
          value: values.amount,
          kind: typeof values.kind === 'string' ? values.kind : 'percentage',
          startDate: values.startDate,
          endDate: values.endDate,
          maxUsage: values.maxUsage
        });
      }}
      validationSchema={PromoterDiscountCodeSchema}
    >
      {({ values, submitForm, setFieldValue }) => {
        const handleChange = (value: string) => {
          setFieldValue('kind', value);
        };
        return (
          <Modal
            type={ModalType.SMALL}
            visible
            onClose={onClose}
            shouldStopModalClose={isLoading}
            title={t('settings.promoterOffers.promoterDiscount.modalTitle')}
            icon={<MdDiscount size={22} />}
            renderFooter={() => (
              <div className="w-full">
                <div className="text-red-500">{errorMessage}</div>
                <Button loading={isLoading} onClick={submitForm}>
                  {t('settings.promoterOffers.promoterDiscount.saveButton')}
                </Button>
              </div>
            )}
          >
            <Form>
              <div className="p-4 pb-52 mt-4 flex flex-col justify-between">
                <p className="mt-2 mb-6 text-sm font-normal">
                  {t('settings.promoterOffers.promoterDiscount.formDescription')}
                </p>

                <div className="flex flex-row">
                  <div className="flex-1 flex-row mr-4">
                    <Field
                      id="kind"
                      name="kind"
                      label={t('settings.promoterOffers.promoterDiscount.discountTypeLabel')}
                      render={() => (
                        <KickbackKindButton
                          kind={values.kind}
                          handleChange={handleChange}
                        />
                      )}
                    />
                  </div>
                  <Field
                    id="amount"
                    name="amount"
                    type="number"
                    label={t('settings.promoterOffers.promoterDiscount.discountAmountLabel')}
                    placeholder={
                      values.kind === 'amount' 
                        ? t('settings.promoterOffers.promoterDiscount.amountPlaceholder')
                        : t('settings.promoterOffers.promoterDiscount.percentagePlaceholder')
                    }
                    disabled={isLoading}
                  />
                </div>
                <Field
                  id="maxUsage"
                  name="maxUsage"
                  type="number"
                  label={t('settings.promoterOffers.promoterDiscount.usageLimitLabel')}
                  placeholder={t('settings.promoterOffers.promoterDiscount.usageLimitPlaceholder')}
                  disabled={isLoading}
                />

                <DateTimeRangePicker
                  minStartDate={new Date().toISOString()}
                  maxEndDate={maxEndDate}
                />
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default PromoterDiscountCodeForm;
